/**
 * Linguadeck App — Single URL for desktop and mobile.
 * Responsive Root: Index uses useIsMobile() (768px breakpoint) to render:
 * - Mobile (< 768px): BottomTabBar, SidebarDrawer, fullscreen card/quiz overlays
 * - Desktop (≥ 768px): SidebarDesktop, inline study modes
 * Shared: GamificationProvider, AuthProvider, Edge TTS, Achievement tracking
 */
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { GamificationProvider } from "@/contexts/GamificationContext";
import CompleteProfileModal from "@/components/CompleteProfileModal";
import AppThemeWrapper from "@/components/AppThemeWrapper";
import Index from "./pages/Index";
import AuthPage from "./pages/AuthPage";
import LandingPage from "./components/landing/LandingPage";
import ReviewsPage from "./pages/ReviewsPage";
import NotFound from "./pages/NotFound";
import SharedDeckPage from "./pages/SharedDeckPage";
import SharedDeckGamePage from "./pages/SharedDeckGamePage";
import SharedFolderPage from "./pages/SharedFolderPage";
import SharedFolderStudyPage from "./pages/SharedFolderStudyPage";
import TeacherLibrary from "./pages/TeacherLibrary";
import LibraryStudyPage from "./pages/LibraryStudyPage";
import GlobalLibraryStudyPage from "./pages/GlobalLibraryStudyPage";
const queryClient = new QueryClient();

/** Shows a loading spinner while Supabase resolves the session */
function LoadingScreen() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "hsl(var(--bg))",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent2)))",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 24,
          animation: "pulse 1.5s ease-in-out infinite",
        }}
      >
        🎴
      </div>
      <div
        style={{
          fontSize: 13,
          color: "hsl(var(--muted-foreground))",
          fontFamily: "DM Sans, sans-serif",
        }}
      >
        Loading StudyDo…
      </div>
    </div>
  );
}

/** Redirects unauthenticated users to /auth. Shows CompleteProfileModal if profile incomplete.
 * Guard: we only hide the modal when isProfileComplete is true (profile has username, birth_date, is_teacher).
 * completeProfile() updates the DB then sets profile state optimistically, so the modal closes immediately. */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, profileLoading, isProfileComplete, completeProfile } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth" replace />;

  if (profileLoading) return <LoadingScreen />;

  if (!isProfileComplete) {
    return (
      <CompleteProfileModal
        user={user}
        onSubmit={completeProfile}
      />
    );
  }

  return <>{children}</>;
}

/** Redirects already-authenticated users away from /auth */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string })?.returnTo;
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to={returnTo || "/"} replace />;
  return <>{children}</>;
}

/** Redirects non-admin users away from admin routes. Requires isAdminUser and isAdminMode. */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdminUser, isAdminMode } = useAuth();
  if (!isAdminUser || !isAdminMode) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes — no auth required; place before protected routes */}
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/reviews" element={<ReviewsPage />} />
      <Route
        path="/auth"
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        }
      />
      {/* Public shared deck / teacher library — no auth required */}
      <Route path="/share/folder/:token/study" element={<SharedFolderStudyPage />} />
      <Route path="/shared/folder/:token/study" element={<SharedFolderStudyPage />} />
      <Route path="/share/folder/:token" element={<SharedFolderPage />} />
      <Route path="/shared/folder/:token" element={<SharedFolderPage />} />
      <Route path="/share/:token/study/:gameMode" element={<SharedDeckGamePage />} />
      <Route path="/shared/:token/study/:gameMode" element={<SharedDeckGamePage />} />
      <Route path="/share/:token" element={<SharedDeckPage />} />
      <Route path="/shared/:token" element={<SharedDeckPage />} />
      <Route path="/library/:teacherId" element={<TeacherLibrary />} />
      <Route path="/library/:teacherId/learn/:deckId" element={<LibraryStudyPage />} />
      <Route path="/library/:teacherId/test/:deckId" element={<LibraryStudyPage />} />
      <Route
        path="/global-library"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      <Route
        path="/global-library/study/:mode/:deckId"
        element={
          <ProtectedRoute>
            <GlobalLibraryStudyPage />
          </ProtectedRoute>
        }
      />

      {/* Protected routes — redirect to /auth if not logged in */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Match game — explicit path so URL is exactly /study/match/:deckId */}
      <Route
        path="/study/match/:deckId"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Other gamified study modes — must be before /study/:deckId */}
      <Route
        path="/study/:gameMode/:deckId"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Study session from home dashboard */}
      <Route
        path="/study/:deckId"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Create deck — full-page flow */}
      <Route
        path="/library/create"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Edit deck — full-page flow */}
      <Route
        path="/library/edit/:deckId"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Gamified study modes from library */}
      <Route
        path="/library/study/:gameMode/:deckId"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Study session from library — must be before /library/:teacherId */}
      <Route
        path="/library/study/:deckId"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Test mode — deck-specific route */}
      <Route
        path="/test/:deckId"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Folder view */}
      <Route
        path="/folder/:folderId"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Gamified study modes from folder */}
      <Route
        path="/folder/:folderId/study/:gameMode/:deckId"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Study session from folder */}
      <Route
        path="/folder/:folderId/study/:deckId"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Dashboard / Statistics */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Gamification (Achievements, Shop, etc.) */}
      <Route
        path="/gamification"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />
      {/* Admin Feedback Management — requires admin mode enabled */}
      <Route
        path="/admin/feedback"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <Index />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support-logs"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <Index />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider delayDuration={0}>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <GamificationProvider>
            <AppThemeWrapper />
            <AppRoutes />
          </GamificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
